import { useEffect, useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";

const STORAGE_KEY = "sda_lead_magnet_state";
const PDF_URL = "/resources/sda-framework.pdf";
const TRIGGER_DELAY_MS = 25000; // show after 25 seconds
const TRIGGER_SCROLL_PCT = 50; // or after 50% scroll, whichever comes first
const COOLDOWN_DAYS = 30;

// Routes where the popup should NEVER show. Best-practice exclusions:
//  - Already on the framework page (the popup IS the framework, redundant)
//  - Admin/host portals (different audience)
//  - Active checkout / purchase confirmation flows (don't disrupt money moments)
//  - Quiz in progress (don't break funnel)
//  - Direct PDF/sitemap/RSS routes
const EXCLUDED_PATHS = [
  /^\/the-framework/,
  /^\/framework/,
  /^\/methodology/,
  /^\/admin/,
  /^\/host/,
  /^\/checkout/,
  /^\/purchase-success/,
  /^\/private-beta\/checkout/,
  /^\/private-beta\/thank-you/,
  /^\/results\/[^/]+\/success/,
  /^\/results\/[^/]+\/upsell/,
  /^\/upsell/,
  /^\/quiz/,
  /^\/assessment\/take/,
  /^\/unsubscribe/,
  /^\/email-preferences/,
  /^\/resources\//,
  /^\/sitemap/,
  /^\/robots/,
  /^\/sage/,
  /^\/cbf/,
  /^\/cbf-leadership-recalibration/,
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reads the popup state from localStorage.
 * Returns one of: "fresh" | "dismissed" | "downloaded".
 * Auto-resets to "fresh" after COOLDOWN_DAYS so dismissed visitors get re-prompted later.
 */
function getStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { status: "fresh" };
    const parsed = JSON.parse(raw);
    if (parsed.status === "downloaded") return parsed; // permanent
    const ageMs = Date.now() - (parsed.ts || 0);
    if (ageMs > COOLDOWN_DAYS * 24 * 60 * 60 * 1000) return { status: "fresh" };
    return parsed;
  } catch (e) {
    return { status: "fresh" };
  }
}

function setStoredState(status) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ status, ts: Date.now() }));
  } catch (e) {
    /* ignore */
  }
}

/**
 * SDA Framework lead-magnet popup.
 * Title: "Why all your mindset work isn't working"
 * Triggers after time-on-site OR scroll-depth, whichever comes first.
 * Captures email, posts to /api/subscribe with source tag, unlocks PDF download.
 */
export default function LeadMagnetPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    // Expose a manual opener for QA / hard-to-reproduce edge cases
    window.__openLeadMagnet = () => {
      window.__POPUP_OPEN = true;
      setOpen(true);
    };

    // Best-practice exclusions: skip on routes where the popup would be redundant or disruptive.
    const path = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
    if (EXCLUDED_PATHS.some((re) => re.test(path))) return;

    const state = getStoredState();
    if (state.status === "downloaded" || state.status === "dismissed") return;

    let firedTimeout = null;
    let firedScroll = false;

    const trigger = () => {
      if (firedScroll) return;
      // Don't collide with any other modal/popup that's already on-screen
      if (document.querySelector('[role="dialog"]') || window.__POPUP_OPEN) return;
      firedScroll = true;
      window.__POPUP_OPEN = true;
      if (firedTimeout) clearTimeout(firedTimeout);
      window.removeEventListener("scroll", onScroll);
      setOpen(true);
    };

    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = (scrollTop / docHeight) * 100;
      if (pct >= TRIGGER_SCROLL_PCT) trigger();
    };

    firedTimeout = setTimeout(trigger, TRIGGER_DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (firedTimeout) clearTimeout(firedTimeout);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const handleClose = () => {
    if (!unlocked) setStoredState("dismissed");
    window.__POPUP_OPEN = false;
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      // Best-effort subscribe, even if backend is down we still let them download
      try {
        await fetch(`${API_URL}/api/subscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            name: name.trim() || undefined,
            source: "sda_framework_lead_magnet",
          }),
        });
      } catch (_) {
        /* swallow, never block the download on a network blip */
      }
      setStoredState("downloaded");
      setUnlocked(true);
      // Auto-trigger the download
      const a = document.createElement("a");
      a.href = PDF_URL;
      a.download = "Stress-Decision-Architecture-Framework.pdf";
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setError("Something went wrong, try again or use the direct download link below.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/65 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-magnet-title"
      data-testid="lead-magnet-popup"
    >
      <div className="relative bg-cream max-w-lg w-full border border-rule shadow-2xl">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-ink/55 hover:text-ink transition-colors"
          aria-label="Close"
          data-testid="lead-magnet-close"
        >
          <X size={20} />
        </button>

        {!unlocked ? (
          <div className="p-8 md:p-10">
            <div className="flex items-center gap-2 mb-5 font-mono text-[10px] tracking-[0.3em] uppercase text-gold">
              <Sparkles size={14} />
              Free Framework
            </div>
            <h2
              id="lead-magnet-title"
              className="font-editorial text-[28px] md:text-[34px] leading-[1.1] font-light text-ink mb-4"
            >
              Why your mindset work
              <em className="block italic text-gold mt-1 font-light">isn't working anymore.</em>
            </h2>
            <p className="text-ink/75 text-[15.5px] leading-[1.55] mb-7 font-light">
              You've done the journaling. The reframes. The affirmations. They worked, until pressure rose past
              a certain point and the same pattern came back. There's a reason. This 9-page framework lays it out
              in plain language.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name (optional)"
                className="w-full bg-white border border-rule px-4 py-3 text-[15px] text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30"
                autoComplete="given-name"
                data-testid="lead-magnet-name"
                disabled={submitting}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full bg-white border border-rule px-4 py-3 text-[15px] text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30"
                autoComplete="email"
                required
                data-testid="lead-magnet-email"
                disabled={submitting}
              />
              {error && (
                <p className="text-[13px] text-red-700 font-light" data-testid="lead-magnet-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-ink text-cream py-3.5 font-mono text-[12px] tracking-[0.18em] uppercase hover:bg-ink-mid transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="lead-magnet-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send me the framework →"
                )}
              </button>
            </form>

            <p className="mt-5 font-mono text-[9.5px] tracking-[0.18em] uppercase text-ink/45 text-center">
              No spam. Unsubscribe any time. 9 pages · 6 min read.
            </p>
          </div>
        ) : (
          <div className="p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/15 border border-gold/30 mb-5">
              <Sparkles size={22} className="text-gold" />
            </div>
            <h2 className="font-editorial text-[28px] md:text-[32px] font-light text-ink mb-3">
              Your download is on its way.
            </h2>
            <p className="text-ink/70 text-[15.5px] leading-[1.55] font-light mb-7">
              The PDF should already be opening in a new tab. If your browser blocked it, use the button below.
            </p>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-ink text-cream px-7 py-3.5 font-mono text-[12px] tracking-[0.18em] uppercase hover:bg-ink-mid transition-colors"
              data-testid="lead-magnet-download-fallback"
            >
              Download the framework PDF
            </a>
            <button
              onClick={handleClose}
              className="block mx-auto mt-6 font-mono text-[10px] tracking-[0.22em] uppercase text-ink/55 hover:text-ink"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
